import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Menu } from '@headlessui/react';
import api from '../../services/api';
import ServiceRequestProgressFlow from '../../components/ServiceRequestProgressFlow';
import { useAuth } from '../../contexts/AuthContext';
import { useTicketPermissions } from '../../hooks/useTicketPermissions';
import { useIsServiceDeskUser } from '../../hooks/useIsServiceDeskUser';
import { trackServiceRequestOperation } from '../../utils/appInsights';
import AttachmentTab from '../../components/AttachmentTab';
import ActivityTab from '../../components/ActivityTab';
import TabLoadingSpinner from '../../components/TabLoadingSpinner';
import OnHoldReasonModal from '../../components/OnHoldReasonModal';
import ReopenReasonModal from '../../components/ReopenReasonModal';
import BreachReasonModal from '../../components/BreachReasonModal';
import RelatedRecordsTab from '../../components/RelatedRecordsTab';
import DuplicateServiceRequestModal from '../../components/DuplicateServiceRequestModal';
import RecordTimeModal from '../../components/RecordTimeModal';
import RichTextEditor from '../../components/RichTextEditor';
import RichTextDisplay from '../../components/RichTextDisplay';
import ResizableTextDisplay from '../../components/ResizableTextDisplay';
import TicketAccessDenied from '../../components/TicketAccessDenied';
import SlaInstancesDisplay, { areAllSlaInstancesTerminal } from '../../components/SlaInstancesDisplay';
import ServiceNowKnownField from '../../components/ServiceNowKnownField';
import { stripHtml, sanitizeNoteInput, hasActualTextContent } from '../../utils/sanitize';
import { fetchAssignmentGroupMembers } from '../../utils/groupMembers';
import {
  impactLabels,
  urgencyLabels,
  priorityLabels,
  caseStateLabels,
  getDisplayLabel
} from '../../utils/labelMappings';
import {
  ArrowLeftIcon,
  PencilIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  DocumentTextIcon,
  TicketIcon,
  ChevronDownIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import { useServiceNowLinkRefetchInterval } from '../../hooks/useServiceNowLinkRefetchInterval';

interface WorkNote {
  id?: string;
  _id?: string;
  User?: {
    firstName?: string;
    lastName?: string;
  };
  author?: {
    firstName?: string;
    lastName?: string;
  };
  note?: string;
  timestamp?: string;
  isPublic?: boolean;
}

const priorityColors: Record<string, string> = {
  'critical_1': 'bg-red-100 text-red-800 border-red-200',
  'high_2': 'bg-orange-100 text-orange-800 border-orange-200',
  'moderate_3': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'low_4': 'bg-blue-100 text-blue-800 border-blue-200',
  'planning_5': 'bg-gray-100 text-gray-800 border-gray-200',
  // Legacy support
  '1': 'bg-red-100 text-red-800 border-red-200',
  '2': 'bg-orange-100 text-orange-800 border-orange-200',
  '3': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  '4': 'bg-blue-100 text-blue-800 border-blue-200',
  '5': 'bg-gray-100 text-gray-800 border-gray-200'
};


const stateColors: Record<string, string> = {
  'new': 'bg-blue-100 text-blue-800',
  'in_progress': 'bg-yellow-100 text-yellow-800',
  'on_hold': 'bg-gray-100 text-gray-800',
  'resolved': 'bg-green-100 text-green-800',
  'closed': 'bg-gray-100 text-gray-800',
  'cancelled': 'bg-red-100 text-red-800'
};

export default function ServiceRequestDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const serviceRequestsListSearch = (location.state as { serviceRequestsListSearch?: string } | null)
    ?.serviceRequestsListSearch;

  const navigateBackToServiceRequests = () => {
    if (serviceRequestsListSearch !== undefined) {
      navigate(`/service-requests${serviceRequestsListSearch}`);
    } else {
      navigate('/service-requests');
    }
  };

  const queryClient = useQueryClient();
  const { user, isContact } = useAuth();
  const isServiceDeskUser = useIsServiceDeskUser();
  const { formatDateTime } = useDateFormatter();
  const [activeTab, setActiveTab] = useState<'details' | 'notes' | 'activities' | 'attachments' | 'related' | 'closure'>('details');
  const [workNoteText, setWorkNoteText] = useState('');
  const [noteType, setNoteType] = useState<'work_note' | 'customer_note'>('work_note');
  const hasLoggedView = useRef(false);
  const previousStateRef = useRef<string | null>(null);

  // Duplicate modal state
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);

  // Record Time modal state
  const [recordTimeModalOpen, setRecordTimeModalOpen] = useState(false);

  // On-hold reason modal state
  const [onHoldModal, setOnHoldModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
  }>({
    isOpen: false,
    title: '',
    message: ''
  });

  // Resolution notes modal state
  const [resolutionModal, setResolutionModal] = useState<{
    isOpen: boolean;
  }>({
    isOpen: false
  });

  // Closure notes modal state
  const [closureModal, setClosureModal] = useState<{
    isOpen: boolean;
  }>({
    isOpen: false
  });

  // Cancellation notes modal state
  const [cancellationModal, setCancellationModal] = useState<{
    isOpen: boolean;
  }>({
    isOpen: false
  });

  // Reopen modal state
  const [reopenModal, setReopenModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
  }>({
    isOpen: false,
    title: '',
    message: ''
  });

  // Breach reason modal state
  const [breachReasonModal, setBreachReasonModal] = useState<{
    isOpen: boolean;
    targetType?: 'RESPONSE' | 'ONGOING' | 'RESOLUTION';
  }>({
    isOpen: false,
    targetType: undefined
  });

  const serviceNowLinkRefetchInterval = useServiceNowLinkRefetchInterval(id, 'SERVICE_REQUEST');

  const { data: serviceRequest, isLoading, error } = useQuery({
    queryKey: ['service-request', id],
    queryFn: async () => {
      const response = await api.get(`/cases/${id}`);
      return response.data;
    },
    refetchInterval: (query) => serviceNowLinkRefetchInterval(query),
  });

  const { data: workNotes = [], isLoading: isLoadingNotes } = useQuery({
    queryKey: ['case', id, 'notes'],
    queryFn: async () => {
      const response = await api.get(`/cases/${id}/work-notes`);
      return response.data;
    },
    enabled: !!id && activeTab === 'notes',
  });

  // Fetch SLA instances for this service request
  const { data: slaInstances } = useQuery({
    queryKey: ['sla-instances', 'SERVICE_REQUEST', id],
    queryFn: async () => {
      const response = await api.get(`/sla-instances/SERVICE_REQUEST/${id}`);
      return response.data;
    },
    enabled: !!id,
    // Stop refetching when all SLAs are in terminal states (BREACHED, ACHIEVED, COMPLETED, CANCELLED)
    refetchInterval: (query) => {
      const data = query.state.data;
      if (areAllSlaInstancesTerminal(data)) {
        return false; // Stop polling
      }
      return 30000; // Continue polling every 30 seconds
    }
  });

  // Get ticket permissions based on backend access control and user role
  const { canEdit, canEditAssignment, isAssignmentOnlyEditMode, canChangeState, canAddWorkNote, canAddAttachments, canAddRelated, canPerformActions, editAccessReason } = useTicketPermissions(
    serviceRequest,
    'cases'
  );

  // Log "Viewed Service Request Details" when service request is loaded
  useEffect(() => {
    if (serviceRequest && user && !hasLoggedView.current) {
      hasLoggedView.current = true;
      previousStateRef.current = serviceRequest.state || serviceRequest.serviceRequestState || null;
      trackServiceRequestOperation(
        'Service Request Details',
        user.email || user.firstName || 'Unknown',
        {
          action: 'Viewed Service Request Details',
          serviceRequestNumber: serviceRequest.number || serviceRequest.id || id || 'Unknown',
        },
        {
          firstName: user.firstName,
          lastName: user.lastName,
          role: user.role,
          roleType: user.roleType,
          roleLevel: user.roleLevel,
          userClass: user.class,
          customerId: user.customerId,
          customerName: user.customerName,
          engagementId: user.engagementId,
          engagementName: user.engagementName,
          isServiceDeskUser: isServiceDeskUser,
        }
      );
    }
  }, [serviceRequest, user, id, isServiceDeskUser]);

  // Fetch current assignment group members
  const { data: currentGroupMembersData } = useQuery({
    queryKey: ['group-members', serviceRequest?.assignmentGroupId, serviceRequest?.customerId || serviceRequest?.Customer?.id],
    queryFn: async () => {
      if (!serviceRequest?.assignmentGroupId) return [];
      try {
        return await fetchAssignmentGroupMembers(
          serviceRequest.assignmentGroupId,
          serviceRequest.customerId || serviceRequest.Customer?.id || null
        );
      } catch (error) {
        console.error('Error fetching group members:', error);
        return [];
      }
    },
    enabled: !!serviceRequest?.assignmentGroupId
  });

  const addWorkNoteMutation = useMutation({
    mutationFn: async (noteData: { note: string; type: 'work_note' | 'customer_note' }) => {
      return api.post(`/cases/${id}/work-notes`, {
        note: noteData.note,
        isPublic: noteData.type === 'customer_note'
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['service-request', id] });
      queryClient.invalidateQueries({ queryKey: ['case', id, 'notes'] });
      queryClient.invalidateQueries({ queryKey: ['case-activities', id] });
      setWorkNoteText('');
      setNoteType('work_note');
      toast.success('Note added successfully');
      
      // Log note addition
      if (user && serviceRequest) {
        const noteTypeLabel = variables.type === 'customer_note' ? 'Customer Note' : 'Work Note';
        trackServiceRequestOperation(
          'Service Request Details - Edit',
          user.email || user.firstName || 'Unknown',
          {
            action: 'Note Added',
            serviceRequestNumber: serviceRequest.number || serviceRequest.id || id || 'Unknown',
            noteType: noteTypeLabel,
          },
          {
          firstName: user.firstName,
          lastName: user.lastName,
          role: user.role,
          roleType: user.roleType,
          roleLevel: user.roleLevel,
          userClass: user.class,
          customerId: user.customerId,
          customerName: user.customerName,
          engagementId: user.engagementId,
          engagementName: user.engagementName,
          isServiceDeskUser: isServiceDeskUser,
          }
        );
      }
    },
    onError: () => {
      toast.error('Failed to add note');
    },
  });

  const updateStateMutation = useMutation({
    mutationFn: async (payload: string | { state: string; onHoldReason?: string; closureNotes?: string; resolutionNotes?: string; cancellationReason?: string; reopenReason?: string }) => {
      // Handle both string and object payload
      const data = typeof payload === 'string' ? { state: payload } : payload;
      const response = await api.put(`/cases/${id}`, data);
      return response.data;
    },
    onSuccess: (updated: any) => {
      // Update cache with new data to prevent error when access is lost
      queryClient.setQueryData(['service-request', id], updated);

      queryClient.invalidateQueries({ queryKey: ['service-request', id] });
      queryClient.invalidateQueries({ queryKey: ['case-activities', id] });
      toast.success('State updated successfully');
      
      // Log state transition
      if (user && serviceRequest) {
        const oldState = previousStateRef.current || serviceRequest.state || 'Unknown';
        const newState = updated.state || updated.serviceRequestState || 'Unknown';
        previousStateRef.current = newState;
        
        trackServiceRequestOperation(
          'Service Request Details - Edit',
          user.email || user.firstName || 'Unknown',
          {
            action: 'Service Request State Updated',
            serviceRequestNumber: serviceRequest.number || serviceRequest.id || id || 'Unknown',
            oldState: oldState,
            newState: newState,
          },
          {
          firstName: user.firstName,
          lastName: user.lastName,
          role: user.role,
          roleType: user.roleType,
          roleLevel: user.roleLevel,
          userClass: user.class,
          customerId: user.customerId,
          customerName: user.customerName,
          engagementId: user.engagementId,
          engagementName: user.engagementName,
          isServiceDeskUser: isServiceDeskUser,
          }
        );
      }
    },
    onError: () => {
      toast.error('Failed to update state');
    },
  });

  // Mutation for Assign To Me
  const assignToMeMutation = useMutation({
    mutationFn: async (data: { assignedToId: string }) => {
      const response = await api.put(`/cases/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-request', id] });
      queryClient.invalidateQueries({ queryKey: ['service-requests'] });
      queryClient.invalidateQueries({ queryKey: ['case-activities', id] });
      toast.success('Service request assigned successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to assign service request');
    }
  });

  // Breach reason mutation
  const breachReasonMutation = useMutation({
    mutationFn: async ({ breachReason, targetType }: { breachReason: string; targetType: 'RESPONSE' | 'ONGOING' | 'RESOLUTION' }) => {
      const response = await api.put(`/cases/${id}/breach-reason`, { breachReason, targetType });
      return response.data;
    },
    onSuccess: async () => {
      // Refetch data first to ensure UI shows updated breach reasons
      await queryClient.refetchQueries({ queryKey: ['service-request', id] });
      // Refetch list queries so breach reason shows without browser refresh
      queryClient.refetchQueries({ queryKey: ['service-requests'] });
      queryClient.refetchQueries({ queryKey: ['cases'] });
      queryClient.invalidateQueries({ queryKey: ['case-activities', id] });
      toast.success('Breach reason saved successfully');
      setBreachReasonModal({ isOpen: false, targetType: undefined });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to save breach reason');
    }
  });

  // Computed values for SLA breach
  const hasSlaBreached = serviceRequest?.slaBreached || slaInstances?.some((sla: any) => sla.isBreached || sla.currentState === 'BREACHED') || false;

  // Check if breach reasons are provided for all breached SLAs
  const breachReasons = {
    response: serviceRequest?.responseBreachReason,
    ongoing: serviceRequest?.ongoingBreachReason,
    resolution: serviceRequest?.resolutionBreachReason
  };

  // Helper to check if a specific SLA target type is breached
  const isSlaTargetBreached = (targetType: string): boolean => {
    if (!slaInstances) return false;
    return slaInstances.some(
      (instance: any) =>
        // Check both instance.targetType and instance.SlaPolicy.targetType for compatibility
        (instance.targetType === targetType || instance.SlaPolicy?.targetType === targetType) &&
        (instance.currentState === 'BREACHED' || instance.isBreached)
    );
  };

  // Check if all breached SLAs have breach reasons
  const hasAllBreachReasons = (): boolean => {
    if (!hasSlaBreached) return true;

    // Check each breached SLA target type for its breach reason
    const responseBreached = isSlaTargetBreached('RESPONSE');
    const ongoingBreached = isSlaTargetBreached('ONGOING');
    const resolutionBreached = isSlaTargetBreached('RESOLUTION');

    if (responseBreached && !serviceRequest?.responseBreachReason) return false;
    if (ongoingBreached && !serviceRequest?.ongoingBreachReason) return false;
    if (resolutionBreached && !serviceRequest?.resolutionBreachReason) return false;

    return true;
  };

  // Legacy check for backward compatibility
  const hasBreachReason = hasAllBreachReasons();

  // Handler for Assign To Me button
  const handleAssignToMe = () => {
    if (user?.id) {
      assignToMeMutation.mutate({ assignedToId: user.id });
    }
  };

  // Check if Assign To Me button should be shown
  const shouldShowAssignToMeButton = () => {
    if (!user?.id || !serviceRequest) return false;
    const currentAssignedToId = serviceRequest.assignedToId || serviceRequest.User_Case_assignedToIdToUser?.id || (serviceRequest.User_Case_assignedToIdToUser as any)?._id;
    if (currentAssignedToId === user.id) return false;
    const isUserInGroup = (currentGroupMembersData || []).some((member: any) =>
      member.id === user.id || member.userId === user.id || member._id === user.id
    );
    // Show button if user is in the group (don't check canEdit - users in the group can assign to themselves)
    return isUserInGroup;
  };

  const addWorkNote = () => {
    const sanitizedNote = sanitizeNoteInput(workNoteText);
    if (hasActualTextContent(sanitizedNote)) {
      // Customer users always create public notes
      const finalNoteType = isContact() ? 'customer_note' : noteType;
      addWorkNoteMutation.mutate({ note: sanitizedNote, type: finalNoteType });
    }
  };

  const handleStateChange = (newState: string) => {
    // Validate mandatory fields before state transition
    if (newState === 'in_progress' && !serviceRequest?.assignedToId) {
      toast.error('Please assign the service request to a user before moving it to In Progress');
      return;
    }

    // Check for breach reason before allowing resolved/closed transitions
    if ((newState === 'resolved' || newState === 'closed') && hasSlaBreached && !hasBreachReason) {
      // Find the first breached SLA without a reason
      let missingTargetType: 'RESPONSE' | 'ONGOING' | 'RESOLUTION' | undefined;
      if (isSlaTargetBreached('RESPONSE') && !serviceRequest?.responseBreachReason) {
        missingTargetType = 'RESPONSE';
      } else if (isSlaTargetBreached('ONGOING') && !serviceRequest?.ongoingBreachReason) {
        missingTargetType = 'ONGOING';
      } else if (isSlaTargetBreached('RESOLUTION') && !serviceRequest?.resolutionBreachReason) {
        missingTargetType = 'RESOLUTION';
      }

      if (missingTargetType) {
        const targetLabel = missingTargetType.charAt(0) + missingTargetType.slice(1).toLowerCase();
        toast.error(`Please provide a breach reason for the ${targetLabel} SLA before resolving or closing this ticket.`);
        setBreachReasonModal({ isOpen: true, targetType: missingTargetType });
        return;
      }
    }

    // Show on-hold modal if transitioning to on_hold state
    if (newState === 'on_hold') {
      setOnHoldModal({
        isOpen: true,
        title: 'Put Service Request On Hold',
        message: 'Please provide a reason for putting this service request on hold:'
      });
      return;
    }

    // Show resolution modal if transitioning to resolved state
    if (newState === 'resolved') {
      setResolutionModal({ isOpen: true });
      return;
    }

    // Show closure modal if transitioning to closed state
    if (newState === 'closed') {
      setClosureModal({ isOpen: true });
      return;
    }

    // Show cancellation modal if transitioning to cancelled state
    if (newState === 'cancelled') {
      setCancellationModal({ isOpen: true });
      return;
    }

    updateStateMutation.mutate(newState);
  };

  const handleConfirmOnHold = (reason: string) => {
    updateStateMutation.mutate({ state: 'on_hold', onHoldReason: reason });
    setOnHoldModal({ isOpen: false, title: '', message: '' });
  };

  const handleCloseOnHoldModal = () => {
    setOnHoldModal({ isOpen: false, title: '', message: '' });
  };

  const handleConfirmResolution = (notes: string) => {
    updateStateMutation.mutate({ state: 'resolved', resolutionNotes: notes });
    setResolutionModal({ isOpen: false });
  };

  const handleCloseResolutionModal = () => {
    setResolutionModal({ isOpen: false });
  };

  const handleConfirmClosure = (notes: string) => {
    updateStateMutation.mutate({ state: 'closed', closureNotes: notes });
    setClosureModal({ isOpen: false });
  };

  const handleCloseClosureModal = () => {
    setClosureModal({ isOpen: false });
  };

  const handleConfirmCancellation = (reason: string) => {
    updateStateMutation.mutate({ state: 'cancelled', cancellationReason: reason });
    setCancellationModal({ isOpen: false });
  };

  const handleCloseCancellationModal = () => {
    setCancellationModal({ isOpen: false });
  };

  const handleReopenClick = () => {
    setReopenModal({
      isOpen: true,
      title: 'Reopen Service Request',
      message: 'Please provide a reason for reopening this service request:'
    });
  };

  const handleConfirmReopen = (reason: string) => {
    // Reopen to new state
    updateStateMutation.mutate({ state: 'new', reopenReason: reason });
    setReopenModal({ isOpen: false, title: '', message: '' });
  };

  const handleCloseReopenModal = () => {
    setReopenModal({ isOpen: false, title: '', message: '' });
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-32 bg-gray-200 rounded"></div>
            <div className="h-48 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !serviceRequest) {
    return (
      <TicketAccessDenied
        ticketType="Service Request"
        ticketNumber={serviceRequest?.number}
        listPageUrl={
          serviceRequestsListSearch !== undefined
            ? `/service-requests${serviceRequestsListSearch}`
            : '/service-requests'
        }
      />
    );
  }

  return (
    <div className="p-6">
      {/* Back button and Actions */}
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={navigateBackToServiceRequests}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="mr-2 h-4 w-4" />
          Back to Service Requests
        </button>

        <div className="flex gap-2">
          {/* Assign To Me Button */}
          {shouldShowAssignToMeButton() && (
            <button
              onClick={handleAssignToMe}
              disabled={assignToMeMutation.isPending}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              {assignToMeMutation.isPending ? 'Assigning...' : 'Assign To Me'}
            </button>
          )}

          {/* Record Time Button - Show when assigned to current user and state is not new/closed/cancelled */}
          {serviceRequest &&
           user &&
           (serviceRequest.User_Case_assignedToIdToUser?.id === user.id || serviceRequest.assignedToId === user.id) &&
           serviceRequest.state !== 'new' &&
           serviceRequest.state !== 'closed' &&
           serviceRequest.state !== 'cancelled' &&
           serviceRequest.Customer &&
           serviceRequest.Engagement &&
           serviceRequest.AssignmentGroup && (
            <button
              onClick={() => setRecordTimeModalOpen(true)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <ClockIcon className="h-5 w-5 mr-2" />
              Record Time
            </button>
          )}

          {/* Actions Dropdown - Show if user can perform actions (not state-restricted) */}
          {canPerformActions && (
          <Menu as="div" className="relative inline-block text-left">
          <div>
            <Menu.Button className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
              <TicketIcon className="h-5 w-5 mr-2" />
              Actions
              <ChevronDownIcon className="ml-2 h-4 w-4" />
            </Menu.Button>
          </div>

          <Menu.Items className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
            <div className="py-1">
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={() => setDuplicateModalOpen(true)}
                    className={`${
                      active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                    } flex items-center w-full px-4 py-2 text-sm`}
                  >
                    <TicketIcon className="h-5 w-5 mr-3" />
                    Duplicate Service Request
                  </button>
                )}
              </Menu.Item>
            </div>
          </Menu.Items>
        </Menu>
          )}
        </div>
      </div>

      {/* Workflow Progress */}
      <div className="mb-6">
        <ServiceRequestProgressFlow
          currentState={serviceRequest.state}
          activities={serviceRequest.CaseActivity || []}
          onStateChange={handleStateChange}
          onReopen={handleReopenClick}
          caseId={id}
          assignedToId={serviceRequest.assignedToId}
          canEdit={canChangeState}
        />
      </div>

      {/* Service Request Header */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start space-x-3 min-w-0 flex-1">
              <div className="flex-shrink-0">
                <DocumentTextIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="min-w-0">
                <h1 className="text-xl font-semibold text-gray-900">
                  {serviceRequest.number}{serviceRequest.shortDescription && <span className="text-base font-normal text-gray-600"> - {stripHtml(serviceRequest.shortDescription)}</span>}
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                  Created on {formatDateTime(serviceRequest.createdAt)}
                  {serviceRequest.createdFromServiceNow && (
                    <span className="ml-2 text-indigo-700">Linked from ServiceNow</span>
                  )}
                  {serviceRequest.syncedToServiceNow && (
                    <span className="ml-2 text-indigo-700">Linked to ServiceNow</span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 flex-wrap flex-shrink-0">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                stateColors[serviceRequest.state] || 'bg-gray-100 text-gray-800'
              }`}>
                {getDisplayLabel(serviceRequest.state, caseStateLabels)}
              </span>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${
                priorityColors[serviceRequest.priority] || 'bg-gray-100 text-gray-800 border-gray-200'
              }`}>
                Priority {priorityLabels[serviceRequest.priority] || serviceRequest.priority}
              </span>
              {(canEdit || canEditAssignment) && (
                <button
                  onClick={() => navigate(`/service-requests/${id}/edit`)}
                  className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  title={editAccessReason}
                >
                  <PencilIcon className="h-4 w-4 mr-1" />
                  Edit
                </button>
              )}
              <ServiceNowKnownField
                placement="header"
                pulseNumber={serviceRequest.number}
                serviceNowNumber={serviceRequest.serviceNowNumber}
                serviceNowKnown={serviceRequest.serviceNowKnown}
                serviceNowLinked={serviceRequest.serviceNowLinked}
                createdFromServiceNow={serviceRequest.createdFromServiceNow}
                syncedToServiceNow={serviceRequest.syncedToServiceNow}
              />
            </div>
          </div>
        </div>

        {/* Show alert if Service Request doesn't have an assigned user */}
        {!serviceRequest.assignedToId && (
          <div className="mb-4 bg-yellow-50 border-l-4 border-yellow-400 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  <strong className="font-medium">Action Required:</strong> This service request needs to be assigned before work can begin. Please assign this service request to a team member to proceed.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* SLA Timers */}
        <SlaInstancesDisplay
          slaInstances={slaInstances}
          onBreachDetected={() => {
            // Trigger immediate refetch when UI detects breach
            queryClient.invalidateQueries({ queryKey: ['sla-instances', 'SERVICE_REQUEST', id] });
          }}
          breachReasons={breachReasons}
          onRequestBreachReason={(targetType) => setBreachReasonModal({ isOpen: true, targetType })}
        />

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex">
            <button
              onClick={() => setActiveTab('details')}
              className={`py-2 px-6 border-b-2 font-medium text-sm ${
                activeTab === 'details'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Details
            </button>
            <button
              onClick={() => setActiveTab('notes')}
              className={`py-2 px-6 border-b-2 font-medium text-sm ${
                activeTab === 'notes'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Notes
            </button>
            <button
              onClick={() => setActiveTab('activities')}
              className={`py-2 px-6 border-b-2 font-medium text-sm ${
                activeTab === 'activities'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Activities
            </button>
            <button
              onClick={() => setActiveTab('attachments')}
              className={`py-2 px-6 border-b-2 font-medium text-sm ${
                activeTab === 'attachments'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Attachments
            </button>
            <button
              onClick={() => setActiveTab('related')}
              className={`py-2 px-6 border-b-2 font-medium text-sm ${
                activeTab === 'related'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Related Records
            </button>
            <button
              onClick={() => setActiveTab('closure')}
              className={`py-2 px-6 border-b-2 font-medium text-sm flex items-center ${
                activeTab === 'closure'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Closure
              {(serviceRequest.state === 'resolved' || serviceRequest.state === 'closed' || serviceRequest.state === 'cancelled') && (
                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                  {serviceRequest.state === 'cancelled' ? '!' : '✓'}
                </span>
              )}
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Details Tab - Using same layout as Changes */}
          {activeTab === 'details' && (
            <div className="space-y-6">
              {/* Row 1: Number and State */}
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Number
                      </label>
                    </div>
                    <div className="col-span-9">
                      <input
                        type="text"
                        value={serviceRequest.number}
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                    </div>
                  </div>
                </div>
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        State
                      </label>
                    </div>
                    <div className="col-span-9">
                      <input
                        type="text"
                        value={getDisplayLabel(serviceRequest.state, caseStateLabels)}
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 2: Impact and Urgency */}
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Impact
                      </label>
                    </div>
                    <div className="col-span-9">
                      <input
                        type="text"
                        value={getDisplayLabel(serviceRequest.impact, impactLabels)}
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                    </div>
                  </div>
                </div>
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Urgency
                      </label>
                    </div>
                    <div className="col-span-9">
                      <input
                        type="text"
                        value={getDisplayLabel(serviceRequest.urgency, urgencyLabels)}
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 3: Priority */}
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Priority
                      </label>
                    </div>
                    <div className="col-span-9">
                      <input
                        type="text"
                        value={(() => {
                          const priority = serviceRequest.priority;
                          const label = priorityLabels[priority];
                          // Extract number from priority like "moderate_3" -> "3"
                          const match = priority?.match(/_(\d)/);
                          const number = match ? match[1] : priority;
                          return label ? `${number} - ${label}` : priority || '-';
                        })()}
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 4: Customer and Engagement */}
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Customer
                      </label>
                    </div>
                    <div className="col-span-9">
                      <input
                        type="text"
                        value={serviceRequest.Customer?.accountName || '-'}
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                    </div>
                  </div>
                </div>
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Engagement
                      </label>
                    </div>
                    <div className="col-span-9">
                      <input
                        type="text"
                        value={serviceRequest.Engagement ? `${serviceRequest.Engagement.name}${serviceRequest.Engagement.status ? ` - ${serviceRequest.Engagement.status}` : ''}` : '-'}
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 5: Contact and Service Plan */}
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Contact
                      </label>
                    </div>
                    <div className="col-span-9">
                      <input
                        type="text"
                        value={serviceRequest.Contact ? `${serviceRequest.Contact.firstName} ${serviceRequest.Contact.lastName}` : '-'}
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                    </div>
                  </div>
                </div>
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Service Plan
                      </label>
                    </div>
                    <div className="col-span-9">
                      <input
                        type="text"
                        value={serviceRequest.ServicePlan?.name || '-'}
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 6: Resource and Resource ID */}
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Resource
                      </label>
                    </div>
                    <div className="col-span-9">
                      <input
                        type="text"
                        value={serviceRequest.otherResourceNames
                          ? 'Others (Custom)'
                          : (serviceRequest.Resource?.name || '-')}
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                    </div>
                  </div>
                </div>
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Resource ID
                      </label>
                    </div>
                    <div className="col-span-9">
                      <input
                        type="text"
                        value={serviceRequest.otherResourceNames
                          ? serviceRequest.otherResourceNames
                          : (serviceRequest.Resource?.resourceId || serviceRequest.resourceIdValue || '-')}
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Reference Alert */}
              {serviceRequest.ReferenceAlert && (
                <div className="grid grid-cols-12 gap-6 mb-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Reference Alert
                        </label>
                      </div>
                      <div className="col-span-9">
                        <Link
                          to={`/alerts/${serviceRequest.ReferenceAlert.id}`}
                          className="inline-flex items-center px-3 py-1 border border-blue-300 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 text-sm h-8"
                        >
                          <TicketIcon className="h-4 w-4 mr-1" />
                          {serviceRequest.ReferenceAlert.number}
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Row 7: Assignment Group and Assigned To */}
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Assignment Group <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <div className="col-span-9">
                      <input
                        type="text"
                        value={serviceRequest.AssignmentGroup?.name || '-'}
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                    </div>
                  </div>
                </div>
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Assigned To
                      </label>
                    </div>
                    <div className="col-span-9 flex gap-2">
                      <input
                        type="text"
                        value={serviceRequest.User_Case_assignedToIdToUser ? `${serviceRequest.User_Case_assignedToIdToUser.firstName || ''} ${serviceRequest.User_Case_assignedToIdToUser.lastName || ''}`.trim() || '-' : '-'}
                        readOnly
                        className="flex-1 px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                      {shouldShowAssignToMeButton() && (
                        <button
                          onClick={handleAssignToMe}
                          disabled={assignToMeMutation.isPending}
                          className="px-3 py-1 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded disabled:opacity-50 whitespace-nowrap h-8"
                        >
                          {assignToMeMutation.isPending ? 'Assigning...' : 'Assign To Me'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 8: Short Description (full width) */}
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-12">
                  <div className="grid grid-cols-12 gap-4 items-start">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1 pt-2">
                        Short Description
                      </label>
                    </div>
                    <div className="col-span-10">
                      <ResizableTextDisplay
                        content={serviceRequest.shortDescription || '-'}
                        defaultHeight={60}
                        minHeight={40}
                        maxHeight={300}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 9: Description (full width) */}
              {serviceRequest.description && (
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-12">
                    <div className="grid grid-cols-12 gap-4 items-start">
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1 pt-2">
                          Description
                        </label>
                      </div>
                      <div className="col-span-10">
                        <ResizableTextDisplay
                          content={serviceRequest.description}
                          defaultHeight={100}
                          minHeight={60}
                          maxHeight={500}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Row 10: On Hold Reason (if on hold) */}
              {serviceRequest.state === 'on_hold' && serviceRequest.onHoldReason && (
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-12">
                    <div className="grid grid-cols-12 gap-4 items-start">
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          On Hold Reason
                        </label>
                      </div>
                      <div className="col-span-10">
                        <div className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-100 text-sm">
                          <RichTextDisplay content={serviceRequest.onHoldReason} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Row 11: Resolution Notes (if resolved or closed) */}
              {(serviceRequest.state === 'resolved' || serviceRequest.state === 'closed') && serviceRequest.resolutionNotes && (
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-12">
                    <div className="grid grid-cols-12 gap-4 items-start">
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Resolution Notes
                        </label>
                      </div>
                      <div className="col-span-10">
                        <div className="w-full px-3 py-2 border border-green-300 rounded bg-green-50 text-sm">
                          <RichTextDisplay content={serviceRequest.resolutionNotes} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Row 12: Closure Notes (if closed) */}
              {serviceRequest.state === 'closed' && serviceRequest.closureNotes && (
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-12">
                    <div className="grid grid-cols-12 gap-4 items-start">
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Closure Notes
                        </label>
                      </div>
                      <div className="col-span-10">
                        <div className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-100 text-sm">
                          <RichTextDisplay content={serviceRequest.closureNotes} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Row 13: Cancellation Reason (if cancelled) */}
              {serviceRequest.state === 'cancelled' && serviceRequest.cancellationReason && (
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-12">
                    <div className="grid grid-cols-12 gap-4 items-start">
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Cancellation Reason
                        </label>
                      </div>
                      <div className="col-span-10">
                        <div className="w-full px-3 py-2 border border-red-300 rounded bg-red-50 text-sm">
                          <RichTextDisplay content={serviceRequest.cancellationReason} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Notes Tab */}
          {activeTab === 'notes' && (
            <div className="bg-white shadow-sm rounded-b-lg border border-t-0 border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h2 className="text-lg font-medium text-gray-900 flex items-center">
                  <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2" />
                  Work Notes & Communication
                </h2>
              </div>

              <div className="p-6">
                {/* Add Note Form */}
                {canAddWorkNote && (
                  <form onSubmit={(e) => { e.preventDefault(); addWorkNote(); }} className="mb-6">
                    <div className="mb-2">
                      <label className="block text-sm font-medium text-gray-700">Add Note</label>
                      <RichTextEditor
                        value={workNoteText}
                        onChange={(value) => setWorkNoteText(value)}
                        placeholder="Enter your note..."
                        minHeight="120px"
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      {!isContact() ? (
                        <div className="flex items-center space-x-4">
                          <label className="flex items-center">
                            <input
                              type="radio"
                              value="work_note"
                              checked={noteType === 'work_note'}
                              onChange={(e) => setNoteType(e.target.value as 'work_note')}
                              className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                            />
                            <span className="text-sm text-gray-700">Work Note</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="radio"
                              value="customer_note"
                              checked={noteType === 'customer_note'}
                              onChange={(e) => setNoteType(e.target.value as 'customer_note')}
                              className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                            />
                            <span className="text-sm text-gray-700">Customer Note</span>
                          </label>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-600 italic">Your notes will be visible to all users</p>
                      )}

                      <button
                        type="submit"
                        disabled={addWorkNoteMutation.isPending || !workNoteText.trim()}
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                      >
                        {addWorkNoteMutation.isPending ? 'Adding...' : 'Add Note'}
                      </button>
                    </div>
                  </form>
                )}

                {/* Notes List */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">Work Notes</h3>
                  {isLoadingNotes ? (
                    <TabLoadingSpinner label="Loading work notes" />
                  ) : workNotes?.length > 0 ? (
                    <div className="space-y-4">
                      {workNotes
                        ?.filter((note: WorkNote) => isContact() ? note.isPublic : true)
                        .map((note: WorkNote) => (
                        <div key={note._id || note.id} className="border border-gray-200 rounded-lg p-4 bg-white">
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex items-center space-x-3">
                              <div className="flex-shrink-0">
                                <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                                  <span className="text-sm font-medium text-gray-600">
                                    {(note.User?.firstName || note.author?.firstName)?.charAt(0).toUpperCase() || 'U'}
                                  </span>
                                </div>
                              </div>
                              <div>
                                <span className="text-sm font-medium text-gray-900">
                                  {note.User ? `${note.User.firstName} ${note.User.lastName}` :
                                   note.author ? `${note.author.firstName || ''} ${note.author.lastName || ''}`.trim() : 'Unknown User'}
                                </span>
                                <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                  note.isPublic ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {note.isPublic ? 'Public' : 'Internal'}
                                </span>
                              </div>
                            </div>
                            <span className="text-xs text-gray-500">
                              {note.timestamp ? formatDateTime(note.timestamp) : ''}
                            </span>
                          </div>
                          <div className="ml-11">
                            <div className="text-sm text-gray-700">
                              <RichTextDisplay content={note.note || ''} />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <ChatBubbleLeftRightIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <p className="mt-2 text-sm text-gray-500">No work notes yet</p>
                      <p className="mt-1 text-xs text-gray-400">Work notes will appear here once added</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Activities Tab */}
          {activeTab === 'activities' && (
            <div className="bg-white shadow rounded-lg p-6">
              <ActivityTab entityId={id!} />
            </div>
          )}

          {/* Attachments Tab */}
          {activeTab === 'attachments' && (
            <div className="bg-white shadow rounded-lg p-6">
              <AttachmentTab
                onAttachmentAdded={() => {
                  // Log attachment addition
                  if (user && serviceRequest) {
                    trackServiceRequestOperation(
                      'Service Request Details - Edit',
                      user.email || user.firstName || 'Unknown',
                      {
                        action: 'Added Attachments',
                        serviceRequestNumber: serviceRequest.number || serviceRequest.id || id || 'Unknown',
                      },
                      {
                        firstName: user.firstName,
                        lastName: user.lastName,
                        role: user.role,
                        roleType: user.roleType,
                        roleLevel: user.roleLevel,
                        userClass: user.class,
                        customerId: user.customerId,
                        customerName: user.customerName,
                        engagementId: user.engagementId,
                        engagementName: user.engagementName,
                        isServiceDeskUser: isServiceDeskUser,
                      }
                    );
                  }
                }}
                onAttachmentDeleted={() => {
                  // Log attachment removal
                  if (user && serviceRequest) {
                    trackServiceRequestOperation(
                      'Service Request Details - Edit',
                      user.email || user.firstName || 'Unknown',
                      {
                        action: 'Removed Attachments',
                        serviceRequestNumber: serviceRequest.number || serviceRequest.id || id || 'Unknown',
                      },
                      {
                        firstName: user.firstName,
                        lastName: user.lastName,
                        role: user.role,
                        roleType: user.roleType,
                        roleLevel: user.roleLevel,
                        userClass: user.class,
                        customerId: user.customerId,
                        customerName: user.customerName,
                        engagementId: user.engagementId,
                        engagementName: user.engagementName,
                        isServiceDeskUser: isServiceDeskUser,
                      }
                    );
                  }
                }}
                entityType="case"
                entityId={id!}
                canUpload={canAddAttachments}
                canDelete={canAddAttachments}
                ticketState={serviceRequest?.state}
              />
            </div>
          )}

          {/* Related Records Tab */}
          {activeTab === 'related' && (
            <div className="bg-white shadow rounded-lg p-6">
              <RelatedRecordsTab
                caseId={id!}
                caseNumber={serviceRequest.number}
                ticketType="SERVICE_REQUEST"
                canManage={canAddRelated}
                onRelatedRecordAdded={() => {
                  // Log related record addition
                  if (user && serviceRequest) {
                    trackServiceRequestOperation(
                      'Service Request Details - Edit',
                      user.email || user.firstName || 'Unknown',
                      {
                        action: 'Added Related Record',
                        serviceRequestNumber: serviceRequest.number || serviceRequest.id || id || 'Unknown',
                      },
                      {
                        firstName: user.firstName,
                        lastName: user.lastName,
                        role: user.role,
                        roleType: user.roleType,
                        roleLevel: user.roleLevel,
                        userClass: user.class,
                        customerId: user.customerId,
                        customerName: user.customerName,
                        engagementId: user.engagementId,
                        engagementName: user.engagementName,
                        isServiceDeskUser: isServiceDeskUser,
                      }
                    );
                  }
                }}
                onRelatedRecordDeleted={() => {
                  // Log related record removal
                  if (user && serviceRequest) {
                    trackServiceRequestOperation(
                      'Service Request Details - Edit',
                      user.email || user.firstName || 'Unknown',
                      {
                        action: 'Remove Related Record',
                        serviceRequestNumber: serviceRequest.number || serviceRequest.id || id || 'Unknown',
                      },
                      {
                        firstName: user.firstName,
                        lastName: user.lastName,
                        role: user.role,
                        roleType: user.roleType,
                        roleLevel: user.roleLevel,
                        userClass: user.class,
                        customerId: user.customerId,
                        customerName: user.customerName,
                        engagementId: user.engagementId,
                        engagementName: user.engagementName,
                        isServiceDeskUser: isServiceDeskUser,
                      }
                    );
                  }
                }}
              />
            </div>
          )}

          {/* Closure Tab */}
          {activeTab === 'closure' && (
            <div className="bg-white shadow rounded-lg p-6">
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 border-b pb-3">Closure Information</h3>

                {/* Resolution Info Grid */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Resolved By</label>
                    <p className="text-sm text-gray-900">
                      {serviceRequest.resolvedBy
                        ? `${serviceRequest.resolvedBy.firstName || ''} ${serviceRequest.resolvedBy.lastName || ''}`.trim() || '-'
                        : '-'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Resolved On</label>
                    <p className="text-sm text-gray-900">
                      {serviceRequest.resolvedAt ? formatDateTime(serviceRequest.resolvedAt) : '-'}
                    </p>
                  </div>
                </div>

                {/* Closure Info Grid */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Closed By</label>
                    <p className="text-sm text-gray-900">
                      {serviceRequest.closedBy
                        ? `${serviceRequest.closedBy.firstName || ''} ${serviceRequest.closedBy.lastName || ''}`.trim() || '-'
                        : '-'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Closed On</label>
                    <p className="text-sm text-gray-900">
                      {serviceRequest.closedAt ? formatDateTime(serviceRequest.closedAt) : '-'}
                    </p>
                  </div>
                </div>

                {/* Resolution Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Resolution Notes
                  </label>
                  {serviceRequest.resolutionNotes ? (
                    <div className="w-full px-4 py-3 border border-green-300 rounded-lg bg-green-50 text-sm">
                      <RichTextDisplay content={serviceRequest.resolutionNotes} />
                    </div>
                  ) : (
                    <div className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-500 italic">
                      No resolution notes provided
                    </div>
                  )}
                </div>

                {/* Closure Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Closure Notes
                  </label>
                  {serviceRequest.closureNotes ? (
                    <div className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-sm">
                      <RichTextDisplay content={serviceRequest.closureNotes} />
                    </div>
                  ) : (
                    <div className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-500 italic">
                      No closure notes provided
                    </div>
                  )}
                </div>

                {/* Cancellation Reason (if cancelled) */}
                {serviceRequest.state === 'cancelled' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cancellation Reason
                    </label>
                    {serviceRequest.cancellationReason ? (
                      <div className="w-full px-4 py-3 border border-red-300 rounded-lg bg-red-50 text-sm">
                        <RichTextDisplay content={serviceRequest.cancellationReason} />
                      </div>
                    ) : (
                      <div className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-500 italic">
                        No cancellation reason provided
                      </div>
                    )}
                    {serviceRequest.cancelledAt && (
                      <div className="mt-1 text-xs text-gray-500">
                        Cancelled on: {formatDateTime(serviceRequest.cancelledAt)}
                      </div>
                    )}
                  </div>
                )}

                {/* Summary Section */}
                {(serviceRequest.state === 'resolved' || serviceRequest.state === 'closed' || serviceRequest.state === 'cancelled') && (
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                          serviceRequest.state === 'cancelled'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {serviceRequest.state === 'resolved' ? 'Resolved' :
                           serviceRequest.state === 'closed' ? 'Closed' : 'Cancelled'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Empty State when no closure info */}
                {serviceRequest.state !== 'resolved' && serviceRequest.state !== 'closed' && serviceRequest.state !== 'cancelled' && (
                  <div className="text-center py-8 text-gray-500">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="mt-2 text-sm">This service request has not been resolved or closed yet.</p>
                    <p className="mt-1 text-xs text-gray-400">Closure information will appear here once the request is resolved.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* On-Hold Reason Modal */}
      <OnHoldReasonModal
        isOpen={onHoldModal.isOpen}
        onClose={handleCloseOnHoldModal}
        onConfirm={handleConfirmOnHold}
        title={onHoldModal.title}
        message={onHoldModal.message}
        confirmLabel="Put On Hold"
        confirmButtonClass="bg-yellow-600 hover:bg-yellow-700 text-white"
        isLoading={updateStateMutation.isPending}
      />

      {/* Resolution Notes Modal */}
      <OnHoldReasonModal
        isOpen={resolutionModal.isOpen}
        onClose={handleCloseResolutionModal}
        onConfirm={handleConfirmResolution}
        title="Resolve Service Request"
        message="Please provide resolution notes:"
        fieldLabel="Resolution Notes"
        placeholder="Enter resolution details (what was done, how the issue was resolved, etc.)..."
        errorMessage="Resolution notes are required"
        confirmLabel="Mark as Resolved"
        confirmButtonClass="bg-green-600 hover:bg-green-700 text-white"
        isLoading={updateStateMutation.isPending}
      />

      {/* Closure Notes Modal */}
      <OnHoldReasonModal
        isOpen={closureModal.isOpen}
        onClose={handleCloseClosureModal}
        onConfirm={handleConfirmClosure}
        title="Close Service Request"
        message="Please provide closure notes:"
        fieldLabel="Closure Notes"
        placeholder="Enter closure notes (resolution details, final outcome, etc.)..."
        errorMessage="Closure notes are required"
        confirmLabel="Close Request"
        confirmButtonClass="bg-gray-600 hover:bg-gray-700 text-white"
        isLoading={updateStateMutation.isPending}
      />

      {/* Cancellation Modal */}
      <OnHoldReasonModal
        isOpen={cancellationModal.isOpen}
        onClose={handleCloseCancellationModal}
        onConfirm={handleConfirmCancellation}
        title="Cancel Service Request"
        message="Please provide a reason for cancellation:"
        fieldLabel="Cancellation Reason"
        placeholder="Enter reason for cancellation (why the request is being cancelled, etc.)..."
        errorMessage="Cancellation reason is required"
        confirmLabel="Cancel Request"
        confirmButtonClass="bg-red-600 hover:bg-red-700 text-white"
        isLoading={updateStateMutation.isPending}
      />

      {/* Duplicate Service Request Modal */}
      <DuplicateServiceRequestModal
        isOpen={duplicateModalOpen}
        onClose={() => setDuplicateModalOpen(false)}
        serviceRequest={serviceRequest}
        onSuccess={(duplicatedServiceRequest: any) => {
          // Log duplicate service request
          if (user) {
            const newServiceRequestNumber = duplicatedServiceRequest?.number || duplicatedServiceRequest?.case?.number || duplicatedServiceRequest?.id || 'Unknown';
            trackServiceRequestOperation(
              'Service Request Created',
              user.email || user.firstName || 'Unknown',
              {
                action: 'Duplicate Service Request',
                serviceRequestNumber: newServiceRequestNumber,
              },
              {
                firstName: user.firstName,
                lastName: user.lastName,
                role: user.role,
                roleType: user.roleType,
                roleLevel: user.roleLevel,
                userClass: user.class,
                customerId: user.customerId,
                customerName: user.customerName,
                engagementId: user.engagementId,
                engagementName: user.engagementName,
                isServiceDeskUser: isServiceDeskUser,
              }
            );
          }
        }}
      />

      {/* Reopen Modal */}
      <ReopenReasonModal
        isOpen={reopenModal.isOpen}
        onClose={handleCloseReopenModal}
        onConfirm={handleConfirmReopen}
        title={reopenModal.title}
        message={reopenModal.message}
        confirmLabel="Reopen Service Request"
        confirmButtonClass="bg-blue-600 hover:bg-blue-700 text-white"
        isLoading={updateStateMutation.isPending}
      />

      {/* Record Time Modal */}
      {serviceRequest && serviceRequest.Customer && serviceRequest.Engagement && serviceRequest.AssignmentGroup && (
        <RecordTimeModal
          isOpen={recordTimeModalOpen}
          onClose={() => setRecordTimeModalOpen(false)}
          onSuccess={() => {
            // Optionally refresh the service request data after recording time
            queryClient.invalidateQueries({ queryKey: ['service-request', id] });
          }}
          ticketNumber={serviceRequest.number || ''}
          customerName={serviceRequest.Customer.accountName}
          customerId={serviceRequest.customerId || serviceRequest.Customer.id}
          engagementName={serviceRequest.Engagement.name}
          engagementId={serviceRequest.engagementId || serviceRequest.Engagement.id}
          groupName={serviceRequest.AssignmentGroup.name}
          groupId={serviceRequest.assignmentGroupId || serviceRequest.AssignmentGroup.id}
        />
      )}

      {/* Breach Reason Modal */}
      <BreachReasonModal
        isOpen={breachReasonModal.isOpen}
        onClose={() => setBreachReasonModal({ isOpen: false, targetType: undefined })}
        onConfirm={(reason, targetType) => {
          if (targetType) {
            breachReasonMutation.mutate({ breachReason: reason, targetType });
          }
        }}
        targetType={breachReasonModal.targetType}
        isLoading={breachReasonMutation.isPending}
      />

    </div>
  );
}